import { linearRegression, schoolMonthIndex } from './statistics';

export const creditsOf = (request) => request.credite_acordate || request.credite_solicitate || 0;

export const estimateCredits = (activity) =>
  activity.credite_acordate ||
  Math.max(1, Math.ceil((Number(activity.credite_per_ora) || 1) * (activity.ore_recomandate || 1)));

export const cosineSimilarity = (a, b) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  let dot = 0;
  let normA = 0;
  let normB = 0;
  keys.forEach((key) => {
    const valueA = a[key] || 0;
    const valueB = b[key] || 0;
    dot += valueA * valueB;
    normA += valueA * valueA;
    normB += valueB * valueB;
  });
  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
};

export const jaccardSimilarity = (setA, setB) => {
  const a = new Set(setA);
  const b = new Set(setB);
  const intersection = [...a].filter((value) => b.has(value)).length;
  const union = new Set([...a, ...b]).size;
  return union === 0 ? 0 : intersection / union;
};

export const buildContext = ({ students, requests, activities, modules, creditTypes }) => {
  const validated = (requests || []).filter((r) => r.status === 'validat');
  const moduleById = new Map((modules || []).map((m) => [m.id, m]));
  const typeById = new Map((creditTypes || []).map((t) => [t.id, t]));
  const moduleCodes = (modules || []).map((m) => m.cod_module);

  const modulesByStudent = new Map();
  const namesByStudent = new Map();
  const activitiesByStudent = new Map();
  const studentsByActivity = new Map();

  (students || []).forEach((student) => {
    modulesByStudent.set(student.id, Object.fromEntries(moduleCodes.map((cod) => [cod, 0])));
    namesByStudent.set(student.id, new Set());
    activitiesByStudent.set(student.id, new Set());
  });

  validated.forEach((request) => {
    const cod = moduleById.get(request.id_module)?.cod_module;
    const vector = modulesByStudent.get(request.id_elev);
    if (vector && cod) vector[cod] += creditsOf(request);
    const names = namesByStudent.get(request.id_elev);
    if (names && request.nume_activitate) names.add(request.nume_activitate);
    const own = activitiesByStudent.get(request.id_elev);
    if (own && request.id_activitate_predefinita) own.add(request.id_activitate_predefinita);
    const key = request.nume_activitate;
    if (key) {
      const ids = studentsByActivity.get(key) || new Set();
      ids.add(request.id_elev);
      studentsByActivity.set(key, ids);
    }
  });

  const activitiesByModule = new Map();
  (activities || []).forEach((activity) => {
    const list = activitiesByModule.get(activity.id_module) || [];
    list.push(activity);
    activitiesByModule.set(activity.id_module, list);
  });

  return {
    students: students || [],
    activities: activities || [],
    modules: modules || [],
    creditTypes: creditTypes || [],
    moduleById,
    typeById,
    moduleCodes,
    modulesByStudent,
    namesByStudent,
    activitiesByStudent,
    studentsByActivity,
    activitiesByModule,
    validated,
    studentById: new Map((students || []).map((s) => [s.id, s])),
    targetSum: (modules || []).reduce((sum, m) => sum + (m.credite_tinta || 0), 0),
  };
};

export const studentTotal = (context, studentId) =>
  Object.values(context.modulesByStudent.get(studentId) || {}).reduce((sum, value) => sum + value, 0);

const activityToSuggestion = (context, activity, motiv, sursa) => {
  const module = context.moduleById.get(activity.id_module);
  const type = context.typeById.get(activity.id_tip_credit);
  return {
    id: activity.id,
    nume: activity.nume_activitate,
    descriere: activity.descriere || '',
    modul: module?.cod_module || '-',
    modulNume: module?.nume_module || '',
    categorie: type?.categorie || 'curricular',
    credite: estimateCredits(activity),
    ore: activity.ore_recomandate || 0,
    motiv,
    sursa,
  };
};

export const ruleBasedSuggestions = (context, studentId) => {
  const suggestions = [];
  const vector = context.modulesByStudent.get(studentId) || {};
  const total = studentTotal(context, studentId);

  context.moduleCodes.forEach((cod) => {
    if ((vector[cod] || 0) === 0) {
      const module = context.modules.find((m) => m.cod_module === cod);
      (context.activitiesByModule.get(module?.id) || [])
        .slice()
        .sort((a, b) => estimateCredits(b) - estimateCredits(a))
        .slice(0, 3)
        .forEach((activity) => suggestions.push(activityToSuggestion(context, activity, `Nu ai nicio activitate in ${cod}`, 'rule')));
    }
  });

  if (context.targetSum > 0 && total >= context.targetSum * 0.8 && total < context.targetSum) {
    suggestions.push({
      id: 'target-close',
      nume: 'Esti aproape de tinta anuala',
      descriere: `Mai ai nevoie de ${context.targetSum - total} credite pentru a atinge tinta.`,
      modul: '-',
      categorie: 'transferabil',
      credite: context.targetSum - total,
      motiv: `Mai ai nevoie de ${context.targetSum - total} credite`,
      sursa: 'rule',
    });
  }

  const allModulesOk = context.moduleCodes.length > 0 && context.moduleCodes.every((cod) => (vector[cod] || 0) >= 5);
  if (allModulesOk) {
    suggestions.push({
      id: 'certificate-ready',
      nume: 'Esti aproape de certificat',
      descriere: 'Toate modulele au cel putin 5 credite.',
      modul: '-',
      categorie: 'transferabil',
      credite: 0,
      motiv: 'Toate modulele au minim 5 credite',
      sursa: 'rule',
    });
  }

  return suggestions;
};

export const gapAnalysisSuggestions = (context, studentId) => {
  const vector = context.modulesByStudent.get(studentId) || {};
  const gaps = context.modules
    .filter((module) => (module.credite_tinta || 0) > 0)
    .map((module) => ({
      module,
      gap: Math.max(0, (module.credite_tinta || 0) - (vector[module.cod_module] || 0)),
    }))
    .filter((entry) => entry.gap > 0)
    .sort((a, b) => b.gap - a.gap);

  return gaps.flatMap((entry) =>
    (context.activitiesByModule.get(entry.module.id) || [])
      .slice()
      .sort((a, b) => estimateCredits(b) - estimateCredits(a))
      .slice(0, 2)
      .map((activity) =>
        activityToSuggestion(context, activity, `Iti lipsesc ${entry.gap} credite in ${entry.module.cod_module}`, 'gap'),
      ),
  );
};

export const collaborativeSuggestions = (context, studentId) => {
  const own = context.modulesByStudent.get(studentId);
  if (!own) return [];

  const similarities = context.students
    .filter((student) => student.id !== studentId)
    .map((student) => ({
      student,
      score: cosineSimilarity(own, context.modulesByStudent.get(student.id) || {}),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  if (similarities.length === 0) return [];
  const neighborIds = new Set(similarities.map((entry) => entry.student.id));

  const activityStats = new Map();
  context.validated.forEach((request) => {
    if (!neighborIds.has(request.id_elev)) return;
    if (context.namesByStudent.get(studentId)?.has(request.nume_activitate)) return;
    const key = request.nume_activitate;
    const current = activityStats.get(key) || { count: 0, request };
    current.count += 1;
    activityStats.set(key, current);
  });

  return [...activityStats.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map((entry) => {
      const activity = context.activities.find((a) => a.nume_activitate === entry.request.nume_activitate);
      const motiv = `${entry.count} elevi din clasa ta au facut asta`;
      if (activity) return activityToSuggestion(context, activity, motiv, 'collab');
      return {
        id: `collab-${entry.request.nume_activitate}`,
        nume: entry.request.nume_activitate,
        descriere: '',
        modul: context.moduleById.get(entry.request.id_module)?.cod_module || '-',
        categorie: context.typeById.get(entry.request.id_tip_credit)?.categorie || 'curricular',
        credite: creditsOf(entry.request),
        motiv,
        sursa: 'collab',
      };
    });
};

export const itemBasedSuggestions = (context, studentId) => {
  const own = context.activitiesByStudent.get(studentId);
  if (!own || own.size === 0) return [];

  const candidates = new Map();
  own.forEach((activityId) => {
    const activityName = context.activities.find((a) => a.id === activityId)?.nume_activitate;
    if (!activityName) return;
    const peers = context.studentsByActivity.get(activityName) || new Set();
    context.activities.forEach((candidate) => {
      if (own.has(candidate.id)) return;
      const candidatePeers = context.studentsByActivity.get(candidate.nume_activitate) || new Set();
      const score = jaccardSimilarity(peers, candidatePeers);
      if (score > 0) {
        const current = candidates.get(candidate.id) || { activity: candidate, score: 0 };
        current.score = Math.max(current.score, score);
        candidates.set(candidate.id, current);
      }
    });
  });

  return [...candidates.values()]
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((entry) => activityToSuggestion(context, entry.activity, 'Elevii care au facut activitati similare au ales si asta', 'item'));
};

export const contentBasedSuggestions = (context, studentId) => {
  const counts = {};
  context.validated
    .filter((request) => request.id_elev === studentId)
    .forEach((request) => {
      const cod = context.moduleById.get(request.id_module)?.cod_module;
      if (cod) counts[cod] = (counts[cod] || 0) + 1;
    });

  const frequent = Object.entries(counts).filter(([, value]) => value >= 3).map(([cod]) => cod);
  if (frequent.length === 0) return [];

  const doneNames = context.namesByStudent.get(studentId) || new Set();
  const suggestions = [];
  frequent.forEach((cod) => {
    const module = context.modules.find((m) => m.cod_module === cod);
    (context.activitiesByModule.get(module?.id) || [])
      .filter((activity) => !doneNames.has(activity.nume_activitate))
      .slice(0, 1)
      .forEach((activity) =>
        activityToSuggestion(context, activity, `Ai deja ${counts[cod]} activitati in ${cod}; continua pe acelasi drum`, 'content'),
      );
  });
  return suggestions;
};

export const predictiveForStudent = (context, studentId) => {
  const credits = new Array(10).fill(0);
  context.validated
    .filter((request) => request.id_elev === studentId)
    .forEach((request) => {
      const index = schoolMonthIndex(request.data_validare || request.data_cerere);
      if (index >= 0 && index < 10) credits[index] += creditsOf(request);
    });
  let running = 0;
  const cumulative = credits.map((value) => {
    running += value;
    return running;
  });
  const now = new Date();
  const currentMonthIndex = Math.min(9, Math.max(0, schoolMonthIndex(now.toISOString())));
  const { slope } = linearRegression(cumulative.slice(0, currentMonthIndex + 1));
  const predicted = Math.max(cumulative[currentMonthIndex], cumulative[currentMonthIndex] + slope * (9 - currentMonthIndex));
  return { slope, predicted, cumulative, currentMonthIndex };
};

export const buildSuggestionsForStudent = (context, studentId) => {
  const combined = [
    ...ruleBasedSuggestions(context, studentId),
    ...gapAnalysisSuggestions(context, studentId),
    ...collaborativeSuggestions(context, studentId),
    ...itemBasedSuggestions(context, studentId),
    ...contentBasedSuggestions(context, studentId),
  ];

  const seen = new Set();
  const unique = [];
  combined.forEach((suggestion) => {
    const key = suggestion.nume;
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(suggestion);
  });

  return unique.slice(0, 5);
};

export const buildProgress = (context, studentId) => {
  const vector = context.modulesByStudent.get(studentId) || {};
  return context.modules.map((module) => ({
    cod: module.cod_module,
    nume: module.nume_module,
    credite: vector[module.cod_module] || 0,
    tinta: module.credite_tinta || 0,
    deficit: Math.max(0, (module.credite_tinta || 0) - (vector[module.cod_module] || 0)),
  }));
};

export const buildPeerSuggestions = (context, studentId) => collaborativeSuggestions(context, studentId);

export const computeCoverage = (context) => {
  if (context.students.length === 0) return 0;
  const withSuggestions = context.students.filter(
    (student) => buildSuggestionsForStudent(context, student.id).length >= 3,
  ).length;
  return (withSuggestions / context.students.length) * 100;
};
